// Global Instructions Rule Applied!
//
// The capital call form's Per-Commitment Allocation table: one row per
// commitment and, per live line item, a Calculated column (read-only) and
// an Override column (the input, empty unless a person overrode the amount)
// under a grouped header carrying the line name - the pair the Call Items
// and Management Fees tables use (spec A3.12 part 2). An overridden row
// shows its calculated amount struck through and the input in the warning
// state; clearing the input is the undo. Then the side-letter fee break /
// waived fee, the row total and "% called" - and a total row with a cell
// under EVERY column.
//
// What a call is sized on decides the commitment columns (spec A3.9
// addendum part 2, cc__call_detail `call.prog__consolidated_basis`):
//   * 'consolidated' - the primary-fund call of a consolidated call (gross
//     or equalization). It is sized on each investor's Consolidated
//     Commitment (its commitment across the member funds), so that comes
//     first and the primary-fund commitment second (secondary text). Rows
//     sort by Consolidated Commitment.
//   * 'transfer' - a satellite call derived by an intercompany transfer. A
//     transfer is sized on the satellite commitment, which stays the main
//     column; the Consolidated Commitment follows for reference.
//   * null - a standalone call: the one Commitment column.
//
// The calculation record (spec A3.12 part 1) adds two read-only columns:
//   * "Fee-paying" - fee rate share x fee basis share, the share of the
//     commitment that pays the fee. Shown when the call has a fee line; the
//     fee line's header carries its rate, so fee = rate x commitment x
//     fee-paying.
//   * "% called" - row total / the commitment the row was sized on
//     (`commitment_amount`). On a consolidated call that IS the
//     Consolidated Commitment, so this column replaces the earlier
//     "% of Consolidated". A call computed before the record existed has no
//     recorded commitment and falls back to the commitment it is sized on
//     today.
//
// Calculated is `calculated_amount` and Override is `override_amount`, on
// every call. The split of a consolidated gross call (by Consolidated
// Commitment) and of a transfer (by its weights) is part of the one
// calculation Compute runs, so it is the calculated amount like any other;
// `override_amount` is written by a person only (spec A3.12, open point 6).
//
// The layout (titles, order) is built once by `allocationLayout` and shared
// with the Excel export (`allocationExport`), so the file has the same
// columns in the same order as the screen.
//
// The table stays the house read/edit table: an AntD Table inside the
// form's FormSection, AntD grouped columns, InputNumber cells.

import React, { useMemo } from 'react'
import { InputNumber, Table, Tag, Tooltip, Typography } from 'antd'
import HelpTip from '../../../../core/components/HelpTip.tsx'

const { Text } = Typography

export type ConsolidatedBasis = 'consolidated' | 'transfer' | null

/** A per-commitment cell as cc__call_detail sends it (passthrough row). */
export interface AllocationCell {
  id: string
  override_amount?: number | null
  calculated_amount?: number | null
  prog__fee_break?: number | null
  /** the commitment the row was sized on (the calculation record) */
  commitment_amount?: number | null
  fee_rate_share?: number | null
  fee_basis_share?: number | null
  fee_paying_capital?: number | null
  [key: string]: unknown
}

export interface AllocationRow {
  key: string
  investor: string | null
  /** the commitment in the call's own fund */
  commitment: number | null
  /** the investor's Consolidated Commitment (null on a standalone commitment) */
  consolidatedCommitment: number | null
  cells: Record<string, AllocationCell | undefined>
}

export interface AllocationItemType {
  id: string
  label: string
  /** a management-fee line: its rows carry the fee-paying shares */
  isFee?: boolean
  /** a fee line's rate as computed (capital_call_items.called_rate), a fraction */
  feeRate?: number | null
}

interface CommitmentColumn {
  key: 'fund' | 'consolidated'
  title: string
  /** rendered as secondary text: shown for reference, not what the call is sized on */
  secondary: boolean
}

export interface AllocationLayout {
  basis: ConsolidatedBasis
  commitmentColumns: CommitmentColumn[]
}

export const PCT_CALLED_TITLE = '% called'
export const FEE_PAYING_TITLE = 'Fee-paying'
export const CALCULATED_TITLE = 'Calculated'
export const OVERRIDE_TITLE = 'Override'
const CONSOLIDATED_COMMITMENT_TITLE = 'Consolidated Commitment'

/** Which commitment columns the call shows, in order. */
export function allocationLayout(basis: ConsolidatedBasis, fundName: string | null | undefined): AllocationLayout {
  const fundTitle = fundName ? `${fundName} Commitment` : 'Fund Commitment'
  if (basis === 'consolidated') {
    return {
      basis,
      commitmentColumns: [
        { key: 'consolidated', title: CONSOLIDATED_COMMITMENT_TITLE, secondary: false },
        { key: 'fund', title: fundTitle, secondary: true }
      ]
    }
  }
  if (basis === 'transfer') {
    return {
      basis,
      commitmentColumns: [
        { key: 'fund', title: fundTitle, secondary: false },
        { key: 'consolidated', title: CONSOLIDATED_COMMITMENT_TITLE, secondary: true }
      ]
    }
  }
  return { basis: null, commitmentColumns: [{ key: 'fund', title: 'Commitment', secondary: false }] }
}

const commitmentValue = (row: AllocationRow, key: CommitmentColumn['key']): number | null =>
  key === 'consolidated' ? row.consolidatedCommitment : row.commitment

const round2 = (v: number): number => Math.round(v * 100) / 100

type CellOverride = (pc: AllocationCell | undefined) => number | null

/** The amount Issue books for a cell: the manual override if there is one, else the calculated amount. */
export function cellEffective(pc: AllocationCell | undefined, pcOverride: CellOverride): number | null {
  if (!pc) return null
  return pcOverride(pc) ?? pc.calculated_amount ?? null
}

/** Rows in display order: by what the call is sized on, largest first. */
export function sortAllocationRows(rows: AllocationRow[], basis: ConsolidatedBasis): AllocationRow[] {
  const size = (r: AllocationRow) => (basis === 'consolidated' ? r.consolidatedCommitment : r.commitment) ?? 0
  return [...rows].sort((a, b) => size(b) - size(a))
}

export function allocationRowTotal(row: AllocationRow, itemTypes: AllocationItemType[], pcOverride: CellOverride): number {
  return round2(itemTypes.reduce((acc, t) => acc + (cellEffective(row.cells[t.id], pcOverride) ?? 0), 0))
}

/**
 * The commitment a row was sized on: the one Compute recorded, else (a call
 * computed before the record existed) the commitment it is sized on today.
 */
export function rowCommitmentUsed(row: AllocationRow, basis: ConsolidatedBasis): number | null {
  for (const pc of Object.values(row.cells)) {
    if (pc?.commitment_amount != null) return pc.commitment_amount
  }
  return basis === 'consolidated' ? row.consolidatedCommitment ?? row.commitment : row.commitment
}

/** Row total / the commitment the row was sized on, as a fraction (null without one). */
export function pctCalled(total: number, commitmentUsed: number | null | undefined): number | null {
  if (!commitmentUsed) return null
  return total / commitmentUsed
}

/** The fee cell of a row that carries the fee-paying shares (null when the call has no record of them). */
function rowFeeCell(row: AllocationRow, itemTypes: AllocationItemType[]): AllocationCell | null {
  for (const t of itemTypes) {
    if (!t.isFee) continue
    const pc = row.cells[t.id]
    if (pc?.fee_rate_share != null && pc?.fee_basis_share != null) return pc
  }
  return null
}

/** Fee rate share x fee basis share: the share of the commitment that pays the fee. */
export function rowFeePaying(row: AllocationRow, itemTypes: AllocationItemType[]): number | null {
  const pc = rowFeeCell(row, itemTypes)
  return pc ? (pc.fee_rate_share as number) * (pc.fee_basis_share as number) : null
}

const fmtPct = (fraction: number | null): string => (fraction == null ? '—' : `${(fraction * 100).toFixed(2)}%`)

/** A line rate: two decimals, up to four when it needs them (0.50%, 0.3541%). */
export function fmtLineRate(fraction: number): string {
  const fixed = (fraction * 100).toFixed(4)
  return `${fixed.replace(/0{1,2}$/, '')}%`
}

/** A line's header: its name, and on a fee line its rate. */
export function lineTitle(t: AllocationItemType): string {
  return t.isFee && t.feeRate != null ? `${t.label} · ${fmtLineRate(t.feeRate)}` : t.label
}

const FEE_PAYING_HELP =
  'The share of the investor’s commitment that pays the fee: its own fee rate ÷ the fund’s rate, × its fee base ÷ its commitment. 100% at the full fee, 50% at half the fee, 0% when the fee is waived. Its fee = the rate in the fee line’s header × its commitment × this share.'
const PCT_CALLED_HELP =
  'Row total ÷ the commitment the amounts were computed on, as at the call date. On a consolidated call that is the Consolidated Commitment.'

interface AllocationData {
  rows: AllocationRow[]
  layout: AllocationLayout
  itemTypes: AllocationItemType[]
  /** a cell's manual override as it stands in the form (unsaved edits included) */
  pcOverride: CellOverride
  hasFeeBreaks: boolean
  feeBreakLabel: string
  rowFeeBreak: (row: AllocationRow) => number
}

/** One total per column, in column order. */
function allocationTotals({ rows, layout, itemTypes, pcOverride, rowFeeBreak }: AllocationData) {
  const commitments = layout.commitmentColumns.map((c) =>
    round2(rows.reduce((acc, row) => acc + (commitmentValue(row, c.key) ?? 0), 0))
  )
  const lines = itemTypes.map((t) => {
    const cells = rows.map((row) => row.cells[t.id])
    return {
      calculated: round2(cells.reduce((acc, pc) => acc + (pc?.calculated_amount ?? 0), 0)),
      effective: round2(cells.reduce((acc, pc) => acc + (cellEffective(pc, pcOverride) ?? 0), 0)),
      overridden: cells.some((pc) => pcOverride(pc) != null)
    }
  })
  const feeBreak = round2(rows.reduce((acc, row) => acc + rowFeeBreak(row), 0))
  const grand = round2(lines.reduce((acc, l) => acc + l.effective, 0))
  const used = rows.reduce((acc, row) => acc + (rowCommitmentUsed(row, layout.basis) ?? 0), 0)
  // the fund's fee-paying share: fee-paying capital over the commitments of
  // the rows that carry a fee record
  let feePayingCapital = 0
  let feeCommitments = 0
  for (const row of rows) {
    const pc = rowFeeCell(row, itemTypes)
    const commitment = pc?.commitment_amount ?? null
    if (!pc || !commitment) continue
    feeCommitments += commitment
    feePayingCapital += pc.fee_paying_capital ?? commitment * (pc.fee_rate_share as number) * (pc.fee_basis_share as number)
  }
  return {
    commitments,
    lines,
    feeBreak,
    grand,
    pct: pctCalled(grand, used),
    feePaying: feeCommitments > 0 ? feePayingCapital / feeCommitments : null
  }
}

type ExportValue = string | number | null

/** The Excel export: the table's columns in the table's order. */
export function allocationExport(data: AllocationData): {
  data: Record<string, ExportValue>[]
  columns: string[]
  percentageColumns: string[]
} {
  const { rows, layout, itemTypes, pcOverride, hasFeeBreaks, feeBreakLabel, rowFeeBreak } = data
  const hasFeeLine = itemTypes.some((t) => t.isFee)
  const calculatedTitle = (t: AllocationItemType) => `${lineTitle(t)}: ${CALCULATED_TITLE}`
  const overrideTitle = (t: AllocationItemType) => `${lineTitle(t)}: ${OVERRIDE_TITLE}`
  const columns = [
    'Investor',
    ...layout.commitmentColumns.map((c) => c.title),
    ...(hasFeeLine ? [FEE_PAYING_TITLE] : []),
    ...itemTypes.flatMap((t) => [calculatedTitle(t), overrideTitle(t)]),
    ...(hasFeeBreaks ? [feeBreakLabel] : []),
    'Total',
    PCT_CALLED_TITLE
  ]
  const out = rows.map((row) => {
    const total = allocationRowTotal(row, itemTypes, pcOverride)
    return {
      Investor: row.investor ?? '',
      ...Object.fromEntries(layout.commitmentColumns.map((c) => [c.title, commitmentValue(row, c.key) ?? 0])),
      ...(hasFeeLine ? { [FEE_PAYING_TITLE]: rowFeePaying(row, itemTypes) } : {}),
      ...Object.fromEntries(
        itemTypes.flatMap((t) => {
          const pc = row.cells[t.id]
          return [
            [calculatedTitle(t), pc?.calculated_amount ?? null],
            // empty unless overridden, as on screen
            [overrideTitle(t), pcOverride(pc)]
          ]
        })
      ),
      ...(hasFeeBreaks ? { [feeBreakLabel]: rowFeeBreak(row) } : {}),
      Total: total,
      [PCT_CALLED_TITLE]: pctCalled(total, rowCommitmentUsed(row, layout.basis))
    }
  })
  return {
    data: out,
    columns,
    percentageColumns: [...(hasFeeLine ? [FEE_PAYING_TITLE] : []), PCT_CALLED_TITLE]
  }
}

interface CallAllocationTableProps extends AllocationData {
  readOnly: boolean
  /** shared currency props for the editable cells (useNumberFormat('currency')) */
  currencyFmt: Record<string, unknown>
  currencyText: (value: number | null | undefined) => string
  /** value null clears the override: the row falls back to its calculated amount */
  setPcOverride: (id: string, value: number | null) => void
  lateCloserCommitmentIds: Set<string>
}

export default function CallAllocationTable({
  rows,
  layout,
  itemTypes,
  pcOverride,
  hasFeeBreaks,
  feeBreakLabel,
  rowFeeBreak,
  readOnly,
  currencyFmt,
  currencyText,
  setPcOverride,
  lateCloserCommitmentIds
}: CallAllocationTableProps): React.ReactElement {
  const hasFeeLine = itemTypes.some((t) => t.isFee)

  const columns = useMemo(
    () => [
      {
        title: 'Investor',
        dataIndex: 'investor',
        width: 220,
        fixed: 'left' as const,
        render: (v: string | null, row: AllocationRow) => (
          <span>
            {v ?? '-'}
            {lateCloserCommitmentIds.has(row.key) ? (
              <Tag color='blue' className='ml-2'>
                late closer
              </Tag>
            ) : null}
          </span>
        )
      },
      ...layout.commitmentColumns.map((c) => ({
        title: c.title,
        dataIndex: `commitment_${c.key}`,
        width: c.key === 'consolidated' ? 190 : 170,
        align: 'right' as const,
        render: (_: unknown, row: AllocationRow) => {
          const value = commitmentValue(row, c.key)
          if (value == null) return <Text type='secondary'>—</Text>
          return c.secondary ? <Text type='secondary'>{currencyText(value)}</Text> : currencyText(value)
        }
      })),
      ...(hasFeeLine
        ? [
            {
              title: (
                <span>
                  {FEE_PAYING_TITLE}
                  <HelpTip title={FEE_PAYING_HELP} />
                </span>
              ),
              dataIndex: 'prog__fee_paying',
              width: 120,
              align: 'right' as const,
              render: (_: unknown, row: AllocationRow) => fmtPct(rowFeePaying(row, itemTypes))
            }
          ]
        : []),
      ...itemTypes.map((t) => ({
        title: lineTitle(t),
        key: t.id,
        children: [
          {
            title: CALCULATED_TITLE,
            dataIndex: `${t.id}__calculated`,
            width: 140,
            align: 'right' as const,
            render: (_: unknown, row: AllocationRow) => {
              const pc = row.cells[t.id]
              if (!pc) return <Text type='secondary'>—</Text>
              const calculated = currencyText(pc.calculated_amount ?? null)
              // overridden: the calculated amount is no longer what is called
              return pcOverride(pc) != null ? (
                <Text delete type='secondary'>
                  {calculated}
                </Text>
              ) : (
                calculated
              )
            }
          },
          {
            title: OVERRIDE_TITLE,
            dataIndex: `${t.id}__override`,
            width: 150,
            align: 'right' as const,
            render: (_: unknown, row: AllocationRow) => {
              const pc = row.cells[t.id]
              if (!pc) return <Text type='secondary'>—</Text>
              const override = pcOverride(pc)
              return (
                <InputNumber
                  size='small'
                  className='w-full'
                  value={override}
                  {...currencyFmt}
                  placeholder='-'
                  disabled={readOnly}
                  status={override != null ? 'warning' : undefined}
                  aria-label={`Override ${t.label} for ${row.investor ?? 'this investor'}`}
                  onChange={(v) => setPcOverride(pc.id, v == null ? null : Number(v))}
                />
              )
            }
          }
        ]
      })),
      ...(hasFeeBreaks
        ? [
            {
              title: feeBreakLabel,
              dataIndex: 'prog__fee_break',
              width: 120,
              align: 'right' as const,
              render: (_: unknown, row: AllocationRow) => {
                const brk = rowFeeBreak(row)
                return brk ? (
                  <Tooltip title='Side-letter fee break: comes off this investor’s own call, not the pool'>
                    <Text type='secondary'>−{currencyText(brk)}</Text>
                  </Tooltip>
                ) : (
                  <Text type='secondary'>—</Text>
                )
              }
            }
          ]
        : []),
      {
        title: 'Total',
        dataIndex: 'prog__row_total',
        width: 140,
        align: 'right' as const,
        render: (_: unknown, row: AllocationRow) => (
          <Text strong>{currencyText(allocationRowTotal(row, itemTypes, pcOverride))}</Text>
        )
      },
      {
        title: (
          <span>
            {PCT_CALLED_TITLE}
            <HelpTip title={PCT_CALLED_HELP} />
          </span>
        ),
        dataIndex: 'prog__pct_called',
        width: 120,
        align: 'right' as const,
        render: (_: unknown, row: AllocationRow) =>
          fmtPct(pctCalled(allocationRowTotal(row, itemTypes, pcOverride), rowCommitmentUsed(row, layout.basis)))
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layout, itemTypes, pcOverride, readOnly, currencyFmt, currencyText, lateCloserCommitmentIds, hasFeeBreaks, feeBreakLabel, rowFeeBreak, hasFeeLine]
  )

  // one total per column, in column order, so every figure sits under its
  // own header
  const totals = useMemo(
    () => allocationTotals({ rows, layout, itemTypes, pcOverride, hasFeeBreaks, feeBreakLabel, rowFeeBreak }),
    [rows, layout, itemTypes, pcOverride, hasFeeBreaks, feeBreakLabel, rowFeeBreak]
  )

  return (
    <Table
      size='small'
      columns={columns}
      dataSource={rows}
      pagination={false}
      rowKey='key'
      scroll={{ x: 'max-content' }}
      summary={() => {
        let index = 0
        return (
          <Table.Summary fixed>
            <Table.Summary.Row className='font-medium'>
              <Table.Summary.Cell index={index++}>Total</Table.Summary.Cell>
              {layout.commitmentColumns.map((c, i) => (
                <Table.Summary.Cell key={`commitment-${c.key}`} index={index++} align='right'>
                  {c.secondary ? (
                    <Text type='secondary'>{currencyText(totals.commitments[i])}</Text>
                  ) : (
                    currencyText(totals.commitments[i])
                  )}
                </Table.Summary.Cell>
              ))}
              {hasFeeLine ? (
                <Table.Summary.Cell key='fee-paying' index={index++} align='right'>
                  {fmtPct(totals.feePaying)}
                </Table.Summary.Cell>
              ) : null}
              {itemTypes.map((t, i) => {
                const line = totals.lines[i]
                // the row rule again: with an override on the line its
                // calculated total is struck through and the total called
                // sits under Override
                return (
                  <React.Fragment key={t.id}>
                    <Table.Summary.Cell index={index++} align='right'>
                      {line.overridden ? (
                        <Text delete type='secondary'>
                          {currencyText(line.calculated)}
                        </Text>
                      ) : (
                        currencyText(line.calculated)
                      )}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={index++} align='right'>
                      {line.overridden ? currencyText(line.effective) : null}
                    </Table.Summary.Cell>
                  </React.Fragment>
                )
              })}
              {hasFeeBreaks ? (
                <Table.Summary.Cell key='fee-break' index={index++} align='right'>
                  {totals.feeBreak ? (
                    <Text type='secondary'>−{currencyText(totals.feeBreak)}</Text>
                  ) : (
                    <Text type='secondary'>—</Text>
                  )}
                </Table.Summary.Cell>
              ) : null}
              <Table.Summary.Cell key='total' index={index++} align='right'>
                {currencyText(totals.grand)}
              </Table.Summary.Cell>
              <Table.Summary.Cell key='pct' index={index++} align='right'>
                {fmtPct(totals.pct)}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )
      }}
    />
  )
}
